/**
 * «Ultime attività» — titolo e data delle ultime voci pubblicate.
 *
 * Erede di «Cosa abbiamo fatto». Due differenze volute (Enrico, 21-08-2026):
 * la riga è **cliccabile** e apre la spiegazione in un pannello laterale, la
 * stessa che si legge nella sezione Attività; e resta sulla Panoramica invece
 * di buttare l'utente su un'altra pagina — chi guarda lo stato del progetto
 * vuole approfondire una riga, non cambiare stanza.
 *
 * Il pannello riusa la forma di `BachecaSidePanel`, che questa pagina già
 * conosce: una sola grammatica per «apri il dettaglio».
 */

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  CircleDot,
  Database,
  FileText,
  Loader2,
  Server,
  Wrench,
  X,
} from "lucide-react";
import type { ComponentType } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeed } from "@/hooks/useFeed";
import { useModuli } from "@/hooks/useModuli";
import { cn } from "@/lib/utils";
import type { FeedTipo, VoceFeed } from "@/types/api";

const FEED_ICON: Record<FeedTipo, ComponentType<{ className?: string }>> = {
  completato: CheckCircle2,
  in_corso: Loader2,
  fix: Wrench,
  dati: Database,
  documenti: FileText,
  infra: Server,
  altro: CircleDot,
};

/** Quante righe stanno nella casella prima di rimandare ad Attività. */
const RIGHE = 7;

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
  });
}

function formatDataLunga(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function UltimeAttivita() {
  const { data: feed = [], isLoading } = useFeed({ limit: RIGHE });
  const { data: moduli = [] } = useModuli();
  const [aperta, setAperta] = useState<VoceFeed | null>(null);

  const modulo = aperta?.modulo_id
    ? moduli.find((m) => m.id === aperta.modulo_id)
    : undefined;

  return (
    <div className="space-y-2">
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Ultime attività
          </span>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        ) : feed.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna attività pubblicata.</p>
        ) : (
          <ul className="divide-y -mx-1">
            {feed.map((v) => {
              const Icon = FEED_ICON[v.tipo];
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setAperta(v)}
                    className={cn(
                      "w-full text-left flex gap-2.5 items-start px-1 py-2.5",
                      "hover:bg-muted/40 transition-colors rounded-sm group"
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-4 shrink-0 mt-0.5 text-muted-foreground",
                        v.tipo === "in_corso" && "animate-spin",
                        v.tipo === "completato" && "text-success"
                      )}
                    />
                    <span className="min-w-0 flex-1 text-sm font-medium leading-snug group-hover:text-brand-700 transition-colors">
                      {v.titolo_pubblicato}
                    </span>
                    {/* Quando il lavoro è stato fatto, non quando lo abbiamo
                        pubblicato: vedi `api/v1/feed.py`. */}
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">
                      {formatData(v.git_committed_at)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Link
        to="/attivita"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand-700 transition-colors group"
      >
        Vedi tutte le attività
        <ArrowRight className="size-3 transition-transform group-hover:translate-x-0.5" />
      </Link>

      <AttivitaSidePanel
        voce={aperta}
        moduloLabel={modulo ? `${modulo.codice} · ${modulo.nome}` : null}
        onClose={() => setAperta(null)}
      />
    </div>
  );
}

// ── Pannello laterale ────────────────────────────────────────────────

function AttivitaSidePanel({
  voce,
  moduloLabel,
  onClose,
}: {
  voce: VoceFeed | null;
  moduloLabel: string | null;
  onClose: () => void;
}) {
  const open = voce !== null;

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && voce && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-foreground/20 z-30 md:hidden"
            onClick={onClose}
            aria-hidden
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            className={cn(
              "fixed inset-y-0 right-0 z-40 w-full md:w-[480px]",
              "bg-background border-l shadow-2xl flex flex-col"
            )}
            role="dialog"
            aria-modal="false"
            aria-label="Dettaglio attività"
          >
            <header className="flex items-start gap-3 border-b px-5 py-4">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-xs text-muted-foreground capitalize">
                  {formatDataLunga(voce.git_committed_at)}
                </p>
                <h2 className="text-lg font-semibold leading-snug tracking-tight">
                  {voce.titolo_pubblicato}
                </h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Chiudi"
                className="shrink-0"
              >
                <X className="size-4" />
              </Button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {voce.corpo_pubblicato ? (
                <p className="text-sm leading-relaxed whitespace-pre-line">
                  {voce.corpo_pubblicato}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nessuna spiegazione aggiuntiva per questa voce.
                </p>
              )}

              {moduloLabel && (
                <div className="pt-2 border-t">
                  <Badge variant="outline">{moduloLabel}</Badge>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
